import React, { type ComponentProps, type ReactNode, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Platform, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import { BodyText, Button, DisplayText, useResponsive } from '@garnish/core';
import { Modal } from '@garnish/overlays';

import { GroupOrderModalCloseButton } from '../GroupOrderModalCloseButton';
import { GroupOrderModalContentBlock } from '../GroupOrderModalContentBlock';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderConfirmationContent = (props: Props) => {
  const {
    closeButtonAccessibilityLabel,
    title,
    body,
    dismissButtonLabel,
    confirmButtonLabel,
    confirmButtonPalette,
    isPerformingAction,
    onClose,
    onConfirm,
  } = props;

  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();
  const { bottom: safeAreaBottomInset } = useSafeAreaInsets();

  const closeButtonLabel =
    closeButtonAccessibilityLabel ??
    formatMessage({
      defaultMessage: 'Close dialog',
      description: 'Group order confirmation | Close button accessibility label',
    });

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    if (isPerformingAction) return;

    onClose();
  }, [isPerformingAction, onClose]);

  // ─── Styles ─────────────────────────────────────────────────────────────────

  const safeAreaBottomStyle = useStyle(() => {
    const shouldUseSafeAreaBottomInset = Platform.OS !== 'web' && currentBreakpoint.isXS;

    if (!shouldUseSafeAreaBottomInset) return;

    return { paddingBottom: safeAreaBottomInset + theme.spacing['2'] };
  }, [currentBreakpoint.isXS, safeAreaBottomInset]);

  const footerStyles = [styles.footer, safeAreaBottomStyle];

  // ────────────────────────────────────────────────────────────────────────

  return (
    <>
      <GroupOrderModalContentBlock style={styles.content}>
        <GroupOrderModalCloseButton accessibilityLabel={closeButtonLabel} onPress={handleClose} />

        <View style={styles.header}>
          <DisplayText accessibilityRole="header" sizeMatch={['32']}>
            {title}
          </DisplayText>

          <BodyText sizeMatch={['16']} style={styles.body}>
            {body}
          </BodyText>
        </View>
      </GroupOrderModalContentBlock>

      <Modal.Footer style={styles.footerContainer} withoutTopBorder>
        <View style={footerStyles}>
          <View>
            <Button
              size="large-wide"
              palette="secondary"
              disabled={isPerformingAction}
              onPress={handleClose}
            >
              {dismissButtonLabel}
            </Button>
          </View>

          <View>
            <Button
              size="large-wide"
              palette={confirmButtonPalette}
              isLoading={isPerformingAction}
              onPress={onConfirm}
            >
              {confirmButtonLabel}
            </Button>
          </View>
        </View>
      </Modal.Footer>
    </>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    gap: theme.spacing['8'],
    paddingTop: theme.spacing['6'],
    paddingBottom: theme.spacing['8'],
  },
  header: {
    gap: theme.spacing['2'],
  },
  body: {
    color: theme.colors.CHARCOAL,
  },
  footerContainer: {
    marginTop: 'auto',
  },
  footer: {
    gap: theme.spacing['4'],
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type Props = {
  closeButtonAccessibilityLabel?: string;
  title: ReactNode;
  body: ReactNode;
  dismissButtonLabel: ReactNode;
  confirmButtonLabel: ReactNode;
  confirmButtonPalette: NonNullable<ComponentProps<typeof Button>['palette']>;
  isPerformingAction: boolean;
  onClose: () => void;
  onConfirm: () => void;
};
